import React, { useState } from 'react';
import { PUBLIC_CONTACT_EMAIL } from '../../constants/landingPublicContact';
import styles from './TermsOfService.module.css';

interface TermsOfServiceProps {
  onAccept: (accepted: boolean) => void;
  required?: boolean;
  className?: string;
  /** Use on dark glass auth backgrounds (e.g. Register). */
  appearance?: 'light' | 'dark';
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ 
  onAccept, 
  required = true, 
  className = '',
  appearance = 'light',
}) => {
  const [accepted, setAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAccepted = e.target.checked;
    setAccepted(isAccepted);
    onAccept(isAccepted);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div
        className={`${styles.termsContainer} ${appearance === 'dark' ? styles.termsContainerDark : ''} ${className}`.trim()}
      >
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={handleCheckboxChange}
            required={required}
            className={styles.checkbox}
          />
          <span className={styles.checkboxText}>
            I agree to the{' '}
            <button 
              type="button"
              onClick={handleLinkClick}
              className={styles.termsLink}
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button 
              type="button"
              onClick={handleLinkClick}
              className={styles.termsLink}
            >
              Privacy Policy
            </button>
            {required && <span className={styles.required}>*</span>}
          </span>
        </label>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Terms & Conditions for Tranzit.io</h2>
              <button 
                className={styles.closeButton}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.section}>
                <p><strong>Effective Date:</strong> April 2025</p>
                
                <p>
                  These Terms & Conditions ("Terms") govern your access to and use of the Tranzit.io platform, services,
                  website, mobile tools, APIs, and all related products (collectively, the "Services"). The Services are
                  owned and operated by SMR Consulting LLC (DBA Tranzit.io), located in Houston, Texas.
                </p>
                <p>
                  By accessing or using Tranzit.io, you agree to be bound by these Terms.
                </p>

                <h4>1. Eligibility & Account Registration</h4>
                <p>
                  You must be at least 18 years old. By creating an account, you agree to provide accurate information,
                  maintain credential security, and accept liability for all activity under your account.
                </p>

                <h4>2. Description of Services</h4>
                <p>
                  Tranzit.io provides logistics workflow tools, communication features, load posting and matching,
                  AI-generated outputs, document management, compliance tools, analytics, and more. Tranzit.io is not
                  a carrier or broker.
                </p>

                <h4>3. Use of AI Features</h4>
                <p>
                  AI may generate summaries, recommendations, or automation. AI output may contain errors. You are
                  responsible for verifying results. Tranzit.io is not liable for decisions made based on AI output.
                </p>

                <h4>4. Billing, Subscriptions & Usage Fees</h4>
                <p>
                  Subscriptions renew automatically. AI usage may incur additional billing. Fees are nonrefundable
                  unless required by law.
                </p>

                <h4>5. User Content Responsibilities</h4>
                <p>
                  You represent that you own the content you upload. You grant Tranzit.io a license to process and store
                  your content solely to provide the Services.
                </p>

                <h4>6. Platform Availability</h4>
                <p>
                  We do not guarantee uninterrupted service. Features may change at any time.
                </p>

                <h4>7. Third-Party Integrations</h4>
                <p>
                  Tranzit.io is not responsible for third-party outages or errors.
                </p>

                <h4>8. Prohibited Conduct</h4>
                <p>
                  You may not misuse, reverse-engineer, interfere with, or use the platform for fraudulent activity.
                </p>

                <h4>9. Disclaimers</h4>
                <p>
                  Services are provided "AS IS." We make no guarantees regarding accuracy, availability, outcomes, or
                  compliance.
                </p>

                <h4>10. Limitation of Liability</h4>
                <p>
                  Liability is limited to the amount paid in the prior 12 months. Tranzit.io is not liable for indirect or
                  consequential damages.
                </p>

                <h4>11. Indemnification</h4>
                <p>
                  You agree to indemnify SMR Consulting LLC (DBA Tranzit.io) from any claims arising from your use of
                  the platform.
                </p>

                <h4>12. Termination</h4>
                <p>
                  We may suspend or terminate access for violations, nonpayment, or compliance concerns.
                </p>

                <h4>13. Governing Law</h4>
                <p>
                  These Terms are governed by Texas law. Arbitration shall occur in Houston, Texas.
                </p>

                <h4>14. Contact Information</h4>
                <p>
                  SMR Consulting LLC (DBA Tranzit.io)<br />
                  Houston, Texas<br />
                  Email: {PUBLIC_CONTACT_EMAIL}
                </p>

                <h4>15. Changes</h4>
                <p>
                  Continued use constitutes acceptance of updated Terms.
                </p>
              </div>

              <div className={styles.section}>
                <h3>Privacy Policy</h3>
                
                <h4>1. Information We Collect</h4>
                <p>We collect information you provide directly to us, such as when you:</p>
                <ul>
                  <li>Create an account</li>
                  <li>Use our services</li>
                  <li>Contact us for support</li>
                  <li>Participate in surveys or promotions</li>
                </ul>

                <h4>2. How We Use Your Information</h4>
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send technical notices, updates, security alerts, and support messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Monitor and analyze trends, usage, and activities</li>
                </ul>

                <h4>3. Information Sharing</h4>
                <p>
                  We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.
                </p>

                <h4>4. Data Security</h4>
                <p>
                  We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                </p>

                <h4>5. Your Rights</h4>
                <p>You have the right to:</p>
                <ul>
                  <li>Access your personal information</li>
                  <li>Correct inaccurate or incomplete information</li>
                  <li>Delete your personal information</li>
                  <li>Object to processing of your personal information</li>
                  <li>Data portability</li>
                </ul>

                <h4>6. Contact Us</h4>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at privacy@tranzit.io
                </p>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={styles.acceptButton}
                onClick={() => {
                  setAccepted(true);
                  onAccept(true);
                  closeModal();
                }}
              >
                I Accept
              </button>
              <button 
                className={styles.declineButton}
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TermsOfService;
